import { useEffect, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Badge } from 'antd';

import { useAppSelector } from 'app/store';
import { useLazyConsultationsQuery } from 'app/services/edpApiV1/consultations';

import { viewerModel } from 'entities/viewer';

import { dataForConsultationsTable, sortingDate } from 'shared/helpers/utils';
import { RequestsSearch, TColumns } from 'shared/types/types';

import { TableInProgress } from 'features/tables';
import { usePullSort } from 'shared/hooks';
import { SortCondition } from 'shared/enums';
import { useIndicators } from 'features/indicators';
import { TConsultationsPreparedData } from 'shared/types/consultations.types';
import { SearchKeysNews } from 'shared/enums/news';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';

export const ConsultationsInProgress = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataConsultations, setDataConsultations] = useState<TConsultationsPreparedData[] | []>([]);
  const [sortInfo, sortTrigger] = usePullSort();
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;
  // Добавляем пуллинг обновления списка и обновляем индикаторы через хук useIndicators

  const [getConsultations, { data, isSuccess, isFetching }] = useLazyConsultationsQuery();

  useEffect(() => {
    getConsultations({
      type: 'myInWork',
      size: currentSize,
      page: currentPage,
      order: sortInfo.value ? sortOption : undefined,
      allAvailableOrganizations,
      ...search,
    });
  }, [currentPage, currentSize, getConsultations, search, sortInfo.value, sortOption, allAvailableOrganizations]);

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 140,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: _ => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'Тема',
      dataIndex: 'topic',
      key: 'topic',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Должность',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: 'Подразделение',
      dataIndex: 'division',
      key: 'division',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span className="d-flex">
          <Badge status={status.type} />
          {status.text}
        </span>
      ),
    },
  ];

  const columnsForHr: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 140,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: _ => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'Тема',
      dataIndex: 'topic',
      key: 'topic',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Должность',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: 'Подразделение',
      dataIndex: 'division',
      key: 'division',
    },
    {
      title: 'Дан ответ',
      dataIndex: 'isHRReplied',
      key: 'isHRReplied',
      width: 102,
      render: (isHRReplied: boolean) => {
        if (isHRReplied) {
          return <CheckOutlined style={{ color: 'green', fontSize: '20px', width: '100%', textAlign: 'center' }} />;
        }
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span className="d-flex">
          <Badge status={status.type} />
          {status.text}
        </span>
      ),
    },
  ];

  useEffect(() => {
    if (data?.data) {
      const dataTable = dataForConsultationsTable(data?.data);

      if (dataTable) {
        setDataConsultations(dataTable);
      }
    }
  }, [data]);

  // подписываемся на обновление индикаторов, после обновления списка и отключаем пуллинг звпроса индикаторов
  useIndicators(isSuccess, isFetching, false);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };

  return (
    <>
      {dataConsultations && (
        <TableInProgress
          dataSource={dataConsultations}
          columns={columnsForHr}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          currentSize={currentSize}
          setCurrentSize={setCurrentSize}
          total={data?.pagination?.total}
          onSearch={onSearch}
        />
      )}
    </>
  );
};
